// =============================================================================
// TwoFactorGate — bramka AAL2 (odpowiednik middleware z Next.js, ale client-side).
// Montowana WEWNĄTRZ ProtectedRoute (user już potwierdzony) i owija AppLayout.
// /setup-2fa jest CELOWO poza tą bramką — inaczej powstałaby pętla redirect.
//
// Fail-closed: błąd odczytu AAL NIE przepuszcza (ekran błędu + Wyloguj).
// =============================================================================

import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { ShieldAlert } from "lucide-react";
import { decide2fa, ENFORCE_2FA, type TwoFactorDecision } from "@/lib/twoFactor";

type GateState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "decided"; decision: TwoFactorDecision };

export function TwoFactorGate({ children }: { children: React.ReactNode }) {
  const { signOut } = useAuth();
  const [state, setState] = useState<GateState>(
    ENFORCE_2FA ? { status: "loading" } : { status: "decided", decision: "bypass" },
  );

  useEffect(() => {
    if (!ENFORCE_2FA) return;
    let cancelled = false;

    (async () => {
      let lastMsg = "Nie udało się zweryfikować statusu 2FA.";
      // 1 retry — chroni przed chwilowym błędem sieci (bez bypassu).
      for (let attempt = 0; attempt < 2; attempt++) {
        const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        if (cancelled) return;
        if (!error && data) {
          setState({
            status: "decided",
            decision: decide2fa({ currentLevel: data.currentLevel, nextLevel: data.nextLevel }),
          });
          return;
        }
        if (error?.message) lastMsg = error.message;
        if (attempt === 0) await new Promise((r) => setTimeout(r, 600));
      }
      if (!cancelled) setState({ status: "error", message: lastMsg });
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  if (state.status === "loading") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md space-y-4 text-center">
          <ShieldAlert className="mx-auto h-10 w-10 text-destructive" />
          <h1 className="text-xl font-semibold">Nie udało się zweryfikować 2FA</h1>
          <p className="text-sm text-muted-foreground">
            Ze względów bezpieczeństwa dostęp jest zablokowany. Odśwież stronę, a jeśli problem wraca — wyloguj się i zaloguj ponownie.
          </p>
          <p className="text-xs text-muted-foreground/70 break-words">{state.message}</p>
          <div className="flex justify-center gap-2">
            <Button variant="outline" onClick={() => window.location.reload()}>
              Odśwież
            </Button>
            <Button variant="destructive" onClick={() => signOut()}>
              Wyloguj
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (state.decision === "setup") return <Navigate to="/setup-2fa" replace />;
  // Ma 2FA, ale zalogowany samym hasłem → krok kodu obsługuje ekran logowania.
  if (state.decision === "code") return <Navigate to="/auth" replace />;

  return <>{children}</>;
}

export default TwoFactorGate;
